// frontend/src/components/FastReact/PlanoTeares.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fastReactAPI } from '../../api/client';
import Toast    from '../Common/Toast';
import { useToast } from '../../hooks/useToast';
import { useTranslation } from '../../context/TranslationContext';
import {
  FiRefreshCw,
  FiChevronDown,
  FiChevronRight,
  FiCheckCircle,
  FiPackage,
  FiPlay,
  FiRotateCcw,
  FiPrinter,
  FiEdit2,
} from 'react-icons/fi';
import SearchableSelect from '../Common/SearchableSelect';
import Pagination from '../Common/Pagination';
import './PlanoTeares.css';

const PAGE_SIZE_DEFAULT = 25;

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'SETUP' },
  { value: 'COMPLETED', label: 'CONCLUIDO' },
  { value: 'READY', label: 'PRONTO' },  
  { value: 'NO_SETUP', label: 'SEM SETUP' },
  { value: 'RESTOCK', label: 'REABASTECER' },
  { value: 'FINISHED', label: 'TERMINADA' },
];

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  const dd = String(dt.getDate()).padStart(2, '0');
  const mo = String(dt.getMonth() + 1).padStart(2, '0');
  const hh = String(dt.getHours()).padStart(2, '0');
  const mi = String(dt.getMinutes()).padStart(2, '0');
  return `${dd}-${mo}-${dt.getFullYear()} ${hh}:${mi}`;
};

const fmtQty = (v) =>
  v != null ? parseFloat(v).toLocaleString('pt-PT', { maximumFractionDigits: 0 }) : '—';

// Static snapshot, not a live countdown: computed once per render pass using
// whatever "now" is at the moment the list is displayed/reloaded — there's
// no setInterval ticking this, so it only changes when the list itself
// refreshes (matches "don't refresh this column all the time").
const RESTOCK_SLA_MINUTES = 5 * 60; // 5h00
const formatRestockTimer = (restockRequestAt) => {
  if (!restockRequestAt) return null;
  const elapsedMs = Date.now() - new Date(restockRequestAt).getTime();
  const remainingMin = RESTOCK_SLA_MINUTES - Math.floor(elapsedMs / 60000);
  const overdue = remainingMin < 0;
  const abs = Math.abs(remainingMin);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return { text: `${overdue ? '-' : ''}${hh}:${mm}`, overdue };
};

const prepStatusBadge = (statusLine) => {
  if (!statusLine) return null;
  if (statusLine === 'SETUP') return <span className="status-badge status--setup">SETUP</span>;
  if (statusLine === 'IN_PROGRESS') return <span className="status-badge status--in_progress">Em Curso</span>;
  if (statusLine === 'READY') return <span className="status-badge status--completed">Pronto</span>;
  if (statusLine === 'COMPLETED') return <span className="status-badge status--completed">Concluido</span>;
  if (statusLine === 'RESTOCK') return <span className="status-badge status--restock">Reabastecer</span>;
  return <span className="status-badge">{statusLine}</span>;
};

const statusAdvanceIcon = (statusLine) => {
  if (statusLine === 'SETUP') return <FiPlay size={15} />;
  if (statusLine === 'RESTOCK') return <FiPlay size={15} />;
  if (statusLine === 'IN_PROGRESS') return <FiCheckCircle size={15} />;
  return null;
};

const PlanoTeares = ({
  embedded      = false,   // hides page header + compact padding
  initialFilters = {},     // overrides default filter values
  readOnly      = false,   // disables advance/revert/print/urgent actions
  hideAdvanceUndo     = false, // hides the start(play)/undo icons specifically
  hideContainerColumn = false, // hides the outer "Carrinho" (container_material) column
  hideUntilFilter = false, // don't auto-load/show the list on mount — filters stay
                           // visible and usable, list appears once the person
                           // actually changes a filter (Restock's "don't show
                           // every completed order by default" requirement)
} = {}) => {
  const { t } = useTranslation();
  const { toasts, removeToast, toast_success, toast_error, toast_warning, toast_warning_confirm } = useToast();

  const [filters, setFilters] = useState({
    machine: [],
    machine_cluster: [],
    status: 'PENDING',
    print_status: 'true',
    po_code: '',
    customer: '',
    date_from: '',
    date_to: '',
    product_code: '',
    variant_code: '',
    lot_no: '',
    ...initialFilters,   // caller can override any defaults
  });

  const [linhaOptions, setLinhaOptions] = useState([]);
  const [machineClusterOptions, setMachineClusterOptions] = useState([]);
  const poDebounce = useRef(null);
  const custDebounce = useRef(null);
  const prodDebounce = useRef(null);
  const varDebounce = useRef(null);
  const lotDebounce = useRef(null);

  const [poInput, setPoInput] = useState('');
  const [custInput, setCustInput] = useState('');
  const [prodInput, setProdInput] = useState('');
  const [varInput, setVarInput] = useState('');
  const [lotInput, setLotInput] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(PAGE_SIZE_DEFAULT);
  const [sortBy, setSortBy] = useState('is_urgent');
  const [sortDir, setSortDir] = useState('desc');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusCounts, setStatusCounts] = useState({ setup: 0, restock: 0 });

  // ── hideUntilFilter: no data fetched/shown until the person actually
  // touches a filter. hasSearched only gates what's RENDERED (and is flipped
  // by handleFilterChange, which already calls loadRows itself); the ref
  // below separately skips just the one automatic load the page/sort/size
  // effect would otherwise fire on mount, so the two concerns don't fight
  // over who triggers the first fetch.
  const [hasSearched, setHasSearched] = useState(!hideUntilFilter);
  const skipInitialLoadRef = useRef(hideUntilFilter);

  const [expanded, setExpanded] = useState(new Set());
  const [prepItems, setPrepItems] = useState({});
  const [loadingPrep, setLoadingPrep] = useState(false);

  const [containerModal, setContainerModal] = useState(null);
  const [containerInput, setContainerInput] = useState('');
  // Print label
  const [printMsg,   setPrintMsg]   = useState('');

  // ── Restock: mark a preparation line as RESTOCK + num_restock_cones ────────
  const [restockModal, setRestockModal] = useState(null); // { poSetupId, preparationId } | null
  const [restockInput, setRestockInput] = useState('');

  // ── Manual edit: container_material ───────────────────────────────────────
  const [editContainerModal, setEditContainerModal] = useState(null); // { poSetupId } | null
  const [editContainerInput, setEditContainerInput] = useState('');

  // ── Manual edit: po_setup.status ──────────────────────────────────────────
  const [editStatusModal, setEditStatusModal] = useState(null); // { poSetupId, current } | null

  const STATUS_EDIT_OPTIONS = [
    { value: 'SETUP',       label: 'SETUP' },
    { value: 'IN_PROGRESS', label: 'Em Preparação' },
    { value: 'READY',       label: 'Pronto' },
    { value: 'COMPLETED',   label: 'Concluido' },
  ];

  useEffect(() => {
    fastReactAPI
      .getPlanoTeares({ page: 1, page_size: 1 })
      .then((r) => {
        if (r.data.machines?.length) setLinhaOptions(r.data.machines);
      })
      .catch(console.error);
  }, []);

  const loadRows = useCallback(
    async (f, p, pp, sb, sd) => {
      setLoading(true);

      try {
        const params = { page: p, page_size: pp };

        if (f.machine?.length) params.machine = f.machine.join(',');
        if (f.machine_cluster?.length) params.machine_cluster = f.machine_cluster.join(',');
        if (f.status) params.status = f.status;
        if (f.po_code) params.po_code = f.po_code;
        if (f.customer) params.customer = f.customer;
        params.print_status = f.print_status ?? 'true';
        if (f.date_from) params.date_from = f.date_from;
        if (f.date_to) params.date_to = f.date_to;
        params.sort_by  = sb;
        params.sort_dir = sd;
        if (f.product_code) params.product_code = f.product_code;
        if (f.variant_code) params.variant_code = f.variant_code;
        if (f.lot_no) params.lot_no = f.lot_no;

        const r = await fastReactAPI.getPlanoTeares(params);
        if (r.data.success) {
          setRows(r.data.data);
          setTotalItems(r.data.total);
          setTotalPages(Math.max(1, Math.ceil(r.data.total / pp)));
          if (r.data.machines?.length) setLinhaOptions(r.data.machines);
          if (r.data.machine_clusters?.length) setMachineClusterOptions(r.data.machine_clusters);
          if (r.data.status_counts) setStatusCounts(r.data.status_counts);
        }
      } catch {
        toast_error(t('messages.error_load', 'Erro ao carregar dados'));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (skipInitialLoadRef.current) {
      skipInitialLoadRef.current = false;
      return;
    }
    loadRows(filters, currentPage, itemsPerPage, sortBy, sortDir);
  }, [currentPage, itemsPerPage, sortBy, sortDir, loadRows]);

  const handleFilterChange = (key, value) => {
    const newF = { ...filters, [key]: value };
    setFilters(newF);
    setCurrentPage(1);
    setHasSearched(true);
    loadRows(newF, 1, itemsPerPage, sortBy, sortDir);
  };

  const handleSort = (col) => {
    const newDir = sortBy === col && sortDir === 'asc' ? 'desc' : 'asc';
    setSortBy(col);
    setSortDir(newDir);
    setCurrentPage(1);
    loadRows(filters, 1, itemsPerPage, col, newDir);
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) {
      return <span style={{ color: 'var(--gray-300)', fontSize: 10 }}> ⇅</span>;
    }
    return <span style={{ color: 'var(--primary)', fontSize: 10 }}> {sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const handleExpand = async (poSetupId) => {
    if (expanded.has(poSetupId)) {
      setExpanded((prev) => {
        const s = new Set(prev);
        s.delete(poSetupId);
        return s;
      });
      return;
    }

    setExpanded((prev) => new Set([...prev, poSetupId]));
    if (prepItems[poSetupId]) return;

    setLoadingPrep(true);
    try {
      const r = await fastReactAPI.getPreparationItems(poSetupId);
      if (r.data.success) {
        setPrepItems((prev) => ({ ...prev, [poSetupId]: r.data.items }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPrep(false);
    }
  };

  const handleToggleUrgent = async (poSetupId, e) => {
    e.stopPropagation();
    try {
      const r = await fastReactAPI.toggleUrgent(poSetupId);
      if (r.data.success) {
        setRows((prev) =>
          prev.map((row) =>
            row.po_setup_id === poSetupId ? { ...row, is_urgent: r.data.is_urgent } : row
          )
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  // When a request uses responseType 'blob'/'arraybuffer' (as PDF downloads
  // do), a failed response's JSON error body still arrives as a Blob rather
  // than parsed JSON — so err.response.data.error is undefined even though
  // the server sent a perfectly good message (e.g. a 403 permission error).
  // This reads the Blob back out as text and parses it, falling back to a
  // generic message only if that's genuinely not possible.
  const parseBlobError = async (err, fallback) => {
    const data = err?.response?.data;
    try {
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        const text = await data.text();
        const json = JSON.parse(text);
        return json?.error || fallback;
      }
      if (data instanceof ArrayBuffer) {
        const text = new TextDecoder('utf-8').decode(data);
        const json = JSON.parse(text);
        return json?.error || fallback;
      }
    } catch {
      return fallback;
    }
    return err?.response?.data?.error || fallback;
  };

  const handlePrintLabel = async (poSetupId) => {
    try {
      const r = await fastReactAPI.streamPrepLabel(poSetupId);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast_success('Etiqueta aberta em nova janela');
    } catch (e) {
      toast_error(await parseBlobError(e, 'Erro ao gerar etiqueta'));
    }
  };

  const handleRevertStatus = async (poSetupId, preparationId) => {
    try {
      const r = await fastReactAPI.revertPreparationStatus(preparationId);
      if (!r.data.success) return;

      const { status_line, is_completed, prepared_count, total_count, po_setup_status } = r.data;

      setPrepItems((prev) => ({
        ...prev,
        [poSetupId]: (prev[poSetupId] || []).map((item) =>
          item.preparation_id === preparationId ? { ...item, status_line } : item
        ),
      }));

      setRows((prev) =>
        prev.map((row) =>
          row.po_setup_id === poSetupId
            ? { ...row, prepared_items: prepared_count, total_items: total_count,
                is_completed, status: po_setup_status ?? row.status }
            : row
        )
      );
    } catch (e) {
      console.error(e);
      toast_error(e.response?.data?.error || 'Erro ao reverter estado');
    }
  };

  // ── Restock: mark line + input num_restock_cones ───────────────────────────
  const handleRestockBtnClick = (poSetupId, preparationId, currentCones) => {
    setRestockInput(currentCones != null ? String(currentCones) : '');
    setRestockModal({ poSetupId, preparationId });
  };

  const handleMarkRestock = async (poSetupId, preparationId, cones) => {
    try {
      const r = await fastReactAPI.markRestock(preparationId, cones);
      if (!r.data.success) return;

      const { status_line, num_restock_cones, po_setup_status, restock_at, restock_by_name } = r.data;

      // container_material is cleared for the WHOLE po_setup on the backend
      // (not just the marked line), so every item under it goes back to
      // needing the "ask on first record" prompt — mirror that here too.
      setPrepItems((prev) => ({
        ...prev,
        [poSetupId]: (prev[poSetupId] || []).map((item) =>
          item.preparation_id === preparationId
            ? { ...item, status_line, num_restock_cones, container_material: null, restock_at, restock_by_name }
            : { ...item, container_material: null }
        ),
      }));

      setRows((prev) =>
        prev.map((row) =>
          row.po_setup_id === poSetupId
            ? { ...row, status: po_setup_status ?? row.status, container_material: null }
            : row
        )
      );

      toast_success('Linha marcada para reabastecimento');
    } catch (e) {
      console.error(e);
      toast_error(e.response?.data?.error || 'Erro ao marcar reabastecimento');
    }
  };

  const handleRestockConfirm = () => {
    if (!restockModal) return;
    const cones = parseInt(restockInput, 10);
    if (!Number.isFinite(cones) || cones < 0) {
      toast_error('Indique um número de cones.');
      return;
    }
    handleMarkRestock(restockModal.poSetupId, restockModal.preparationId, cones);
    setRestockModal(null);
  };

  const handleAdvanceStatus = async (poSetupId, preparationId, containerMaterial = null) => {
    try {
      const r = await fastReactAPI.advancePreparationStatus(preparationId, containerMaterial);
      if (!r.data.success) return;

      const { status_line, is_completed, prepared_count, total_count,
              container_material, po_setup_status, was_restock } = r.data;

      setPrepItems((prev) => ({
        ...prev,
        [poSetupId]: (prev[poSetupId] || []).map((item) =>
          item.preparation_id === preparationId
            ? { ...item, status_line, container_material }
            : item
        ),
      }));

      setRows((prev) =>
        prev.map((row) =>
          row.po_setup_id === poSetupId
            ? {
                ...row,
                prepared_items: prepared_count,
                total_items: total_count,
                is_completed,
                status: po_setup_status ?? row.status,
                container_material: container_material ?? row.container_material,
              }
            : row
        )
      );

      // Auto-queue label when last item completes — except when this
      // completion came from advancing a restocked line (no label needed).
      if (is_completed && !was_restock) handlePrintLabel(poSetupId);
    } catch (e) {
      console.error(e);
      toast_error(e.response?.data?.error || 'Erro ao avançar estado');
    }
  };

  const handleAdvanceBtnClick = (poSetupId, preparationId, currentStatusLine) => {
    if (currentStatusLine !== 'SETUP') {
      handleAdvanceStatus(poSetupId, preparationId);
      return;
    }

    const row = rows.find((r) => r.po_setup_id === poSetupId);
    if (row?.container_material) {
      handleAdvanceStatus(poSetupId, preparationId, row.container_material);
    } else {
      setContainerInput('');
      setContainerModal({ poSetupId, preparationId });
    }
  };

  const handleContainerConfirm = () => {
    if (!containerModal) return;
    const val = containerInput.trim() || null;
    handleAdvanceStatus(containerModal.poSetupId, containerModal.preparationId, val);
    setContainerModal(null);
  };

  // ── Manual edit: container_material ───────────────────────────────────────
  const openEditContainer = (row) => {
    setEditContainerInput(row.container_material || '');
    setEditContainerModal({ poSetupId: row.po_setup_id });
  };

  const handleEditContainerSave = async () => {
    if (!editContainerModal) return;
    const { poSetupId } = editContainerModal;
    const value = editContainerInput.trim() || null;
    try {
      const r = await fastReactAPI.updateContainerMaterial(poSetupId, value);
      const saved = r.data.container_material;

      setRows((prev) =>
        prev.map((row) =>
          row.po_setup_id === poSetupId ? { ...row, container_material: saved } : row
        )
      );
      // Keep the expanded prep table in sync without needing a refetch
      setPrepItems((prev) => ({
        ...prev,
        [poSetupId]: (prev[poSetupId] || []).map((item) => ({
          ...item,
          container_material: saved,
        })),
      }));

      toast_success('Carrinho atualizado');
      setEditContainerModal(null);
    } catch (e) {
      toast_error(e.response?.data?.error || 'Erro ao atualizar carrinho');
    }
  };

  // ── Manual edit: po_setup.status (cascades to prepared lines) ─────────────
  const openEditStatus = (row) => {
    setEditStatusModal({ poSetupId: row.po_setup_id, current: row.status });
  };

  const applyStatusChange = async (poSetupId, newStatus) => {
    try {
      const r = await fastReactAPI.updatePoSetupStatus(poSetupId, newStatus);
      const { status, is_completed, completed_at, updated_prep_rows } = r.data;

      setRows((prev) =>
        prev.map((row) =>
          row.po_setup_id === poSetupId
            ? { ...row, status, is_completed, completed_at }
            : row
        )
      );
      // Cascade locally to prepared lines only (prepared_by set) — matches backend rule
      setPrepItems((prev) => ({
        ...prev,
        [poSetupId]: (prev[poSetupId] || []).map((item) =>
          item.prepared_by ? { ...item, status_line: status } : item
        ),
      }));

      toast_success(
        updated_prep_rows > 0
          ? `Status atualizado para ${status} (${updated_prep_rows} linha(s) preparada(s) atualizadas)`
          : `Status atualizado para ${status}`
      );
    } catch (e) {
      toast_error(e.response?.data?.error || 'Erro ao atualizar status');
    }
  };

  const handleSelectNewStatus = (newStatus) => {
    if (!editStatusModal) return;
    const { poSetupId, current } = editStatusModal;
    setEditStatusModal(null);
    if (newStatus === current) return;

    toast_warning_confirm(
      `Alterar status de "${current || '—'}" para "${newStatus}"? ` +
      `Esta ação também atualiza as linhas de preparação já iniciadas.`,
      () => applyStatusChange(poSetupId, newStatus)
    );
  };

  return (
    <>
      <div className="plano-teares-list">
        {!embedded && <div className="page-header page-header--compact">
          <h2>{t('plano_teares.title', 'Plano Teares')}</h2>
          <div className="pt-status-quick-filters">
            <button
              type="button"
              className={`pt-quick-filter-btn ${filters.status === 'PENDING' ? 'pt-quick-filter-btn--active' : ''}`}
              onClick={() => handleFilterChange('status', 'PENDING')}
            >
              SETUP
              <span className="pt-quick-filter-badge">{statusCounts.setup}</span>
            </button>
            <button
              type="button"
              className={`pt-quick-filter-btn pt-quick-filter-btn--restock ${filters.status === 'RESTOCK' ? 'pt-quick-filter-btn--active' : ''}`}
              onClick={() => handleFilterChange('status', 'RESTOCK')}
            >
              REABASTECER
              <span className="pt-quick-filter-badge">{statusCounts.restock}</span>
            </button>
          </div>
          <div className="page-header-actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => { setHasSearched(true); loadRows(filters, currentPage, itemsPerPage, sortBy, sortDir); }}
              disabled={loading}
              title="Atualizar"
            >
              <FiRefreshCw size={14} className={loading ? 'pt-spin' : ''} />
            </button>
          </div>
        </div>}

        <div className="list-filters">
          <div className="filters-row">
            <div className="filter-group" style={{ minWidth: 110 }}>
              <label>Print Status</label>
              <select
                value={filters.print_status}
                onChange={(e) => handleFilterChange('print_status', e.target.value)}
              >
                <option value="true">SIM</option>
                <option value="false">Todos</option>
              </select>
            </div>

            <div className="filter-group filter-group-sm">
              <label>{t('plano_teares.machine', 'Máquina')}</label>
              <SearchableSelect
                multi
                value={filters.machine}
                onChange={(v) => handleFilterChange('machine', v)}
                options={linhaOptions.map((l) => ({ value: l, label: l }))}
                getOptionLabel={(o) => o.label}
                getOptionValue={(o) => o.value}
                placeholder={t('all', 'Todas')}
              />
            </div>

            <div className="filter-group filter-group-sm">
              <label>{t('plano_teares.machine_cluster', 'Núcleo')}</label>
              <SearchableSelect
                multi
                value={filters.machine_cluster}
                onChange={(v) => handleFilterChange('machine_cluster', v)}
                options={machineClusterOptions.map((c) => ({ value: c, label: c }))}
                getOptionLabel={(o) => o.label}
                getOptionValue={(o) => o.value}
                placeholder={t('all', 'Todos')}
              />
            </div>

            <div className="filter-group filter-group-sm">
              <label>{t('plano_teares.status', 'Estado')}</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">{t('all', 'Todos')}</option>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group filter-group-sm">
              <label>{t('plano_teares.ordem', 'Ordem')}</label>
              <div className="filter-search-wrap">
                <input
                  type="text"
                  className="filter-search-input"
                  placeholder={t('search', 'Pesquisar…')}
                  value={poInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPoInput(v);
                    clearTimeout(poDebounce.current);
                    poDebounce.current = setTimeout(() => handleFilterChange('po_code', v), 350);
                  }}
                />
                {poInput && (
                  <span
                    className="filter-search-clear"
                    onClick={() => {
                      setPoInput('');
                      clearTimeout(poDebounce.current);
                      handleFilterChange('po_code', '');
                    }}
                  >
                    ×
                  </span>
                )}
              </div>
            </div>

            <div className="filter-group filter-group-md">
              <label>{t('plano_teares.customer', 'Cliente')}</label>
              <div className="filter-search-wrap">
                <input
                  type="text"
                  className="filter-search-input"
                  placeholder={t('search', 'Pesquisar…')}
                  value={custInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCustInput(v);
                    clearTimeout(custDebounce.current);
                    custDebounce.current = setTimeout(() => handleFilterChange('customer', v), 350);
                  }}
                />
                {custInput && (
                  <span
                    className="filter-search-clear"
                    onClick={() => {
                      setCustInput('');
                      clearTimeout(custDebounce.current);
                      handleFilterChange('customer', '');
                    }}
                  >
                    ×
                  </span>
                )}
              </div>
            </div>

            <div className="filter-group">
              <label>Setup De</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>Setup Até</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
              />
            </div>

            <div className="filter-group filter-group-sm">
              <label>{t('plano_teares.product_code', 'Produto')}</label>
              <div className="filter-search-wrap">
                <input
                  type="text"
                  className="filter-search-input"
                  placeholder={t('search', 'Pesquisar…')}
                  value={prodInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    setProdInput(v);
                    clearTimeout(prodDebounce.current);
                    prodDebounce.current = setTimeout(() => handleFilterChange('product_code', v), 350);
                  }}
                />
                {prodInput && (
                  <span
                    className="filter-search-clear"
                    onClick={() => {
                      setProdInput('');
                      clearTimeout(prodDebounce.current);
                      handleFilterChange('product_code', '');
                    }}
                  >
                    ×
                  </span>
                )}
              </div>
            </div>

            <div className="filter-group filter-group-sm">
              <label>{t('plano_teares.variant_code', 'Variante')}</label>
              <div className="filter-search-wrap">
                <input
                  type="text"
                  className="filter-search-input"
                  placeholder={t('search', 'Pesquisar…')}
                  value={varInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    setVarInput(v);
                    clearTimeout(varDebounce.current);
                    varDebounce.current = setTimeout(() => handleFilterChange('variant_code', v), 350);
                  }}
                />
                {varInput && (
                  <span
                    className="filter-search-clear"
                    onClick={() => {
                      setVarInput('');
                      clearTimeout(varDebounce.current);
                      handleFilterChange('variant_code', '');
                    }}
                  >
                    ×
                  </span>
                )}
              </div>
            </div>

            <div className="filter-group filter-group-sm">
              <label>{t('plano_teares.lot_no', 'Lote')}</label>
              <div className="filter-search-wrap">
                <input
                  type="text"
                  className="filter-search-input"
                  placeholder={t('search', 'Pesquisar…')}
                  value={lotInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLotInput(v);
                    clearTimeout(lotDebounce.current);
                    lotDebounce.current = setTimeout(() => handleFilterChange('lot_no', v), 350);
                  }}
                />
                {lotInput && (
                  <span
                    className="filter-search-clear"
                    onClick={() => {
                      setLotInput('');
                      clearTimeout(lotDebounce.current);
                      handleFilterChange('lot_no', '');
                    }}
                  >
                    ×
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="table-container">
          <div className="table-scroll">
            {!hasSearched ? (
              <div className="pt-state-msg">
                <FiPackage size={28} style={{ color: 'var(--gray-300)' }} />
                <span>Indique um filtro para ver as ordens correspondentes.</span>
              </div>
            ) : loading ? (
              <div className="pt-state-msg">
                <FiRefreshCw size={20} className="pt-spin" />
                <span>A carregar…</span>
              </div>
            ) : rows.length === 0 ? (
              <div className="pt-state-msg">
                <FiPackage size={28} style={{ color: 'var(--gray-300)' }} />
                <span>{t('no_data', 'Sem ordens para os filtros selecionados')}</span>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: 26 }}></th>
                    <th style={{ width: 30, cursor: 'pointer' }} onClick={() => handleSort('machine_cluster')}>
                      Núcleo<SortIcon col="machine_cluster" />
                    </th>
                    <th style={{ width: 70, cursor: 'pointer' }} onClick={() => handleSort('machine')}>
                      Máquina<SortIcon col="machine" />
                    </th>
                    <th style={{ width: 130, cursor: 'pointer' }} onClick={() => handleSort('po_code_line_full')}>
                      Ordem<SortIcon col="po_code_line_full" />
                    </th>
                    <th style={{ width: 130, cursor: 'pointer' }} >Produto</th>
                    <th style={{ width: 130, cursor: 'pointer' }} onClick={() => handleSort('customer_name')}>
                      Cliente<SortIcon col="customer_name" />
                    </th>
                    <th style={{ width: 110, cursor: 'pointer' }} onClick={() => handleSort('setup_date')}>
                      Data Setup<SortIcon col="setup_date" />
                    </th>
                    <th style={{ width: 72 }} className="text-right">{t('plano_teares.qty_total', 'Total')}</th>
                    <th style={{ width: 72 }} className="text-right">{t('plano_teares.qty_pending', 'Pend.')}</th>
                    <th style={{ width: 36, cursor:'pointer' }} onClick={() => handleSort('is_urgent')}>🚩<SortIcon col="is_urgent" /></th>
                    <th style={{ width: 64 }}>Print</th>
                    {!hideContainerColumn && <th style={{ width: 64 }}>Carr.</th>}
                    <th style={{ width: 95 }}>{t('plano_teares.status', 'Status')}</th>
                    <th style={{ width: 120 }}>{t('plano_teares.preparation', 'Preparação')}</th>
                    <th style={{ width: 170 }}>Índices</th>                    
                  </tr>
                </thead>
                <tbody>
                  {rows.reduce((acc, row, idx) => {
                    const prevRow = idx > 0 ? rows[idx - 1] : null;
                    const machineBreak = prevRow && prevRow.machine !== row.machine;
                    const isExp = expanded.has(row.po_setup_id);
                    const items = prepItems[row.po_setup_id] || [];
                    const p = row.total_items > 0 ? Math.round((row.prepared_items / row.total_items) * 100) : 0;

                    acc.push(
                      <React.Fragment key={row.po_setup_id}>
                        <tr
                          className={`clickable ${row.is_completed ? 'pt-row-done' : ''} ${machineBreak ? 'pt-row-machine-break' : ''}`}
                          onClick={() => handleExpand(row.po_setup_id)}
                        >
                          <td className="pt-td-toggle">
                            {isExp ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                          </td>

                          <td className="pt-td-badge">
                            {row.machine_cluster ? (
                              <span className="pt-cluster-badge">{row.machine_cluster}</span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>

                          <td className="pt-td-badge">
                            {row.machine ? (
                              <span className="pt-linha-badge">{row.machine}</span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>

                          <td>
                            <span className="pt-po-code">{row.po_code_line_full}</span>
                          </td>



                          <td>
                            <span className="pt-product">{row.product_name || '—'}</span>
                            {row.product_description && (
                              <span className="pt-product-desc">{row.product_description}</span>
                            )}
                          </td>

                          <td className="text-muted">{row.customer_name || '—'}</td>

                          <td>
                            {row.status === 'RESTOCK' && row.restock_request_at ? (
                              (() => {
                                const timer = formatRestockTimer(row.restock_request_at);
                                return (
                                  <span
                                    className={`pt-restock-timer ${timer.overdue ? 'pt-restock-timer--overdue' : ''}`}
                                    title="Tempo restante (5h00 desde o pedido de reabastecimento)"
                                  >
                                    {timer.text}
                                  </span>
                                );
                              })()
                            ) : (
                            <span className="pt-date">
                              {row.setup_date ? new Date(row.setup_date).toLocaleDateString('pt-PT') : '—'}
                            </span>
                            )}
                          </td>

                          <td className="text-right pt-qty">
                            {fmtQty((parseFloat(row.strip_quantity) || 0) + (parseFloat(row.strip_qty_made) || 0))}
                          </td>

                          <td className="text-right pt-qty">
                            {fmtQty(row.strip_quantity)}
                          </td>

                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: readOnly ? 'default' : 'pointer',
                                fontSize: 15,
                                padding: '2px 4px',
                                opacity: row.is_urgent ? 1 : 0.2,
                              }}
                              onClick={readOnly ? undefined : (e) => handleToggleUrgent(row.po_setup_id, e)}
                              title={
                                readOnly
                                  ? (row.is_urgent ? 'Urgente' : '')
                                  : (row.is_urgent ? 'Urgente — clicar para remover' : 'Marcar urgente')
                              }
                            >
                              🚩
                            </button>
                          </td>

                          <td>
                            {row.print_status ? (
                              <span style={{ color: 'var(--primary)', fontSize: 14 }}>✓</span>
                            ) : (
                              <span style={{ color: 'var(--gray-300)', fontSize: 14 }}>—</span>
                            )}
                          </td>

                          {!hideContainerColumn && (
                          <td className="pt-mono">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span>{row.container_material || '—'}</span>
                              {!readOnly && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); openEditContainer(row); }}
                                  title="Editar carrinho"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer',
                                           color: 'var(--gray-400)', padding: 2, display: 'flex' }}
                                >
                                  <FiEdit2 size={11} />
                                </button>
                              )}
                            </div>
                          </td>
                          )}
                          <td>
                            {/* Always trust po_setup.status first — is_completed is only
                                a fallback for legacy rows where status was never set */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              {row.status === 'COMPLETED' ? (
                                <span className="status-badge status--completed">
                                  <FiCheckCircle size={10} style={{ marginRight: 3 }} />
                                  Concluido
                                </span>
                              ) : row.status === 'READY' ? (
                                <span className="status-badge status--completed">Pronto</span>
                              ) : row.status === 'IN_PROGRESS' ? (
                                <span className="status-badge status--in_progress">Em Preparação</span>
                              ) : row.status === 'SETUP' ? (
                                <span className="status-badge status--setup">SETUP</span>
                              ) : row.status === 'RESTOCK' ? (
                                <span className="status-badge status--restock">Reabastecer</span>
                              ) : row.status === 'FINISHED' ? (
                                <span className="status-badge status--finished">
                                  <FiCheckCircle size={10} style={{ marginRight: 3 }} />
                                  Terminada
                                </span>
                              ) : row.is_completed ? (
                                <span className="status-badge status--completed">
                                  <FiCheckCircle size={10} style={{ marginRight: 3 }} />
                                  Concluido
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                              {!readOnly && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); openEditStatus(row); }}
                                  title="Editar status"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer',
                                           color: 'var(--gray-400)', padding: 2, display: 'flex' }}
                                >
                                  <FiEdit2 size={11} />
                                </button>
                              )}
                            </div>
                          </td>

                          <td>
                            {row.total_items > 0 ? (
                              <div className="pt-prep-cell">
                                <div className="pt-prog-bar">
                                  <div className="pt-prog-fill" style={{ width: `${p}%` }} />
                                </div>
                                <span className="pt-prog-label">{row.prepared_items}/{row.total_items}</span>
                              </div>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>

                          <td>
                            {row.indices ? (
                              <span className="pt-indices-cell" title={row.indices}>{row.indices}</span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>

                        </tr>

                        {isExp && (
                          <tr className="pt-expand-tr">
                            <td colSpan={13} className="pt-expand-td">
                              <div className="pt-expand-inner">
                                <div style={{ display: 'flex', gap: 16, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                  {row.container_material && (
                                    <div className="pt-indices-block" style={{ marginBottom: 0 }}>
                                      <span className="pt-indices-label">Carrinho:</span>
                                      <span className="pt-indices-val">{row.container_material}</span>
                                    </div>
                                  )}
                                  {row.indices && (
                                    <div className="pt-indices-block" style={{ marginBottom: 0 }}>
                                      <span className="pt-indices-label">Índices:</span>
                                      <span className="pt-indices-val">{row.indices}</span>
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    style={{ marginLeft: 'auto' }}
                                    onClick={(e) => { e.stopPropagation(); handlePrintLabel(row.po_setup_id); }}
                                  disabled={readOnly}
                                  style={{ opacity: readOnly ? 0 : 1, pointerEvents: readOnly ? 'none' : 'auto' }}
                                  title="Imprimir etiqueta"
                                  >
                                    <FiPrinter size={13} style={{ marginRight: 4 }} />
                                    Etiqueta
                                  </button>
                                </div>

                                {loadingPrep && !items.length ? (
                                  <p className="pt-prep-msg">A carregar…</p>
                                ) : !items.length ? (
                                  <p className="pt-prep-msg">Sem itens de preparação.</p>
                                ) : (
                                  <table className="pt-prep-table">
                                    <thead>
                                      <tr>
                                        <th style={{ width: 112 }}></th>
                                        <th style={{ width: 60 }}>Máq.</th>
                                        <th>Produto</th>
                                        <th style={{ width: 90 }}>Variante</th>
                                        <th style={{ width: 150 }}>Lote</th>
                                        <th style={{ textAlign: 'right', width: 70 }}>Qtd</th>
                                        <th style={{ width: 100 }}>Estado</th>
                                        <th style={{ width: 80 }}>Cones</th>
                                        <th style={{ width: 90 }}>Carrinho</th>
                                        <th style={{ width: 110 }}>Localização</th>
                                        <th>Preparado por</th>
                                        <th style={{ width: 130 }}>Data prep.</th>
                                        <th>Reabastecido por</th>
                                        <th style={{ width: 130 }}>Data reab.</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {items.map((item) => (
                                        <tr
                                          key={item.preparation_id}
                                          className={item.status_line === 'COMPLETED' ? 'pt-prep-row--done' : ''}
                                        >
                                          <td>
                                            <div className="pt-actions-row">
                                              {!readOnly && !hideAdvanceUndo && statusAdvanceIcon(item.status_line) && (
                                                <button
                                                  type="button"
                                                  className="pt-advance-btn"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAdvanceBtnClick(row.po_setup_id, item.preparation_id, item.status_line);
                                                  }}
                                                  title={item.status_line === 'SETUP' ? 'Iniciar preparação' : item.status_line === 'RESTOCK' ? 'Iniciar reabastecimento' : 'Marcar como concluído'}
                                                >
                                                  {statusAdvanceIcon(item.status_line)}
                                                </button>
                                              )}
                                              {!readOnly && !hideAdvanceUndo && item.status_line !== 'SETUP' && (
                                                <button
                                                  type="button"
                                                  className="pt-advance-btn pt-advance-btn--undo"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRevertStatus(row.po_setup_id, item.preparation_id);
                                                  }}
                                                  title="Desfazer"
                                                >
                                                  <FiRotateCcw size={15} />
                                                </button>
                                              )}
                                              {!readOnly && item.status_line === 'COMPLETED' && (
                                                <button
                                                  type="button"
                                                  className="pt-advance-btn pt-advance-btn--restock"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRestockBtnClick(row.po_setup_id, item.preparation_id, item.num_restock_cones);
                                                  }}
                                                  title="Marcar para reabastecimento"
                                                >
                                                  <FiPackage size={15} />
                                                </button>
                                              )}
                                            </div>
                                          </td>

                                          <td className="pt-td-badge">
                                            {item.machine ? (
                                              <span className="pt-linha-badge" style={{ fontSize: 10 }}>{item.machine}</span>
                                            ) : (
                                              <span className="text-muted">—</span>
                                            )}
                                          </td>

                                          <td>
                                            <span className="pt-prep-code">{item.product_code}</span>
                                            {item.product_description && (
                                              <span className="pt-prep-desc">{item.product_description}</span>
                                            )}
                                          </td>

                                          <td className="text-muted">{item.variant_code || '—'}</td>
                                          <td className="pt-mono">{item.lot_no}</td>
                                          <td className="text-right pt-mono">
                                            {item.quantity != null ? parseFloat(item.quantity).toFixed(1) : '—'}
                                          </td>
                                          <td>{prepStatusBadge(item.status_line)}</td>
                                          <td className="text-right pt-mono">{item.num_restock_cones ?? '—'}</td>
                                          <td className="pt-mono">{item.container_material || '—'}</td>
                                          <td className="pt-mono" style={{ fontSize: 11 }}>{item.location_position || '—'}</td>
                                          <td className="text-muted">{item.prepared_by_name || '—'}</td>
                                          <td className="pt-date">
                                            {item.prepared_at ? formatDate(item.prepared_at) : '—'}
                                          </td>
                                          <td className="text-muted">{item.restock_by_name || '—'}</td>
                                          <td className="pt-date">
                                            {item.restock_at ? formatDate(item.restock_at) : '—'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );

                    return acc;
                  }, [])}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(v) => {
              setItemsPerPage(v);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast key={toast.id} id={toast.id} message={toast.message}
            type={toast.type} duration={toast.duration}
            requireConfirm={toast.requireConfirm} onConfirm={toast.onConfirm}
            onClose={removeToast} />
        ))}
      </div>

      {restockModal && (
        <div className="pt-modal-overlay" onClick={() => setRestockModal(null)}>
          <div className="pt-modal" onClick={(e) => e.stopPropagation()}>
            <h4 className="pt-modal-title">Reabastecer</h4>
            <p className="pt-modal-sub">Indique o número de cones.</p>
            <input
              type="number"
              min="0"
              step="1"
              className="pt-modal-input"
              placeholder="Ex: 12"
              value={restockInput}
              onChange={(e) => setRestockInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRestockConfirm();
              }}
              autoFocus
            />
            <div className="pt-modal-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setRestockModal(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleRestockConfirm}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {containerModal && (
        <div className="pt-modal-overlay" onClick={() => setContainerModal(null)}>
          <div className="pt-modal" onClick={(e) => e.stopPropagation()}>
            <h4 className="pt-modal-title">Identificar Carrinho</h4>
            <p className="pt-modal-sub">Indique o número do carrinho para esta preparação.</p>
            <input
              type="text"
              className="pt-modal-input"
              placeholder="Ex: C-001"
              value={containerInput}
              onChange={(e) => setContainerInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleContainerConfirm();
              }}
              autoFocus
            />
            <div className="pt-modal-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setContainerModal(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleContainerConfirm}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit container_material (manual edit, propagates to all prep rows) */}
      {editContainerModal && (
        <div className="pt-modal-overlay" onClick={() => setEditContainerModal(null)}>
          <div className="pt-modal" onClick={(e) => e.stopPropagation()}>
            <h4 className="pt-modal-title">Editar Carrinho</h4>
            <p className="pt-modal-sub">
              Atualiza o carrinho da ordem e de todas as linhas de preparação.
            </p>
            <input
              type="text"
              className="pt-modal-input"
              placeholder="Ex: C-001"
              value={editContainerInput}
              onChange={(e) => setEditContainerInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleEditContainerSave();
              }}
              autoFocus
            />
            <div className="pt-modal-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setEditContainerModal(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleEditContainerSave}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit po_setup.status — selecting a value triggers a toast confirm
          before anything is saved (handleSelectNewStatus) */}
      {editStatusModal && (
        <div className="pt-modal-overlay" onClick={() => setEditStatusModal(null)}>
          <div className="pt-modal" onClick={(e) => e.stopPropagation()}>
            <h4 className="pt-modal-title">Editar Status</h4>
            <p className="pt-modal-sub">
              Altera o status da ordem. Linhas já preparadas serão atualizadas
              para o mesmo status.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              {STATUS_EDIT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSelectNewStatus(opt.value)}
                  style={{
                    textAlign: 'left',
                    padding: '10px 14px',
                    borderRadius: 8,
                    border: opt.value === editStatusModal.current
                      ? '2px solid var(--primary)'
                      : '1px solid var(--gray-300)',
                    background: opt.value === editStatusModal.current
                      ? 'var(--primary-light, #e0f2f1)'
                      : 'white',
                    fontWeight: opt.value === editStatusModal.current ? 700 : 500,
                    cursor: 'pointer',
                  }}
                >
                  {opt.label}
                  {opt.value === editStatusModal.current && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--gray-500)' }}>
                      (atual)
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="pt-modal-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setEditStatusModal(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlanoTeares;